import { ScanLine, Search } from "lucide-react";
import type { InputHTMLAttributes } from "react";

import { cn } from "@/lib/cn";

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Shows the barcode affordance on the right, as in both designs. */
  onScanClick?: () => void;
  containerClassName?: string;
}

/**
 * The product search field from both screens.
 *
 * The barcode button is a visible affordance, not the scanning mechanism. A USB
 * HID scanner types its payload as a burst of keystrokes into whatever has focus,
 * so Phase 4 listens at the document level and does not require this field to be
 * focused at all. The button is for a cashier without a scanner, or with a
 * damaged barcode — it opens manual entry.
 */
export function SearchInput({
  onScanClick,
  className,
  containerClassName,
  ...props
}: SearchInputProps) {
  return (
    <div
      className={cn(
        "flex h-touch-lg items-center gap-2 rounded-control border border-line bg-surface px-3",
        "focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20",
        containerClassName,
      )}
    >
      <Search className="size-5 shrink-0 text-ink-400" aria-hidden />

      <input
        type="search"
        className={cn(
          "min-w-0 flex-1 bg-transparent text-sm text-ink-900 outline-none",
          "placeholder:text-ink-400",
          // Safari draws its own clear button on type=search, which collides
          // with the scan affordance.
          "[&::-webkit-search-cancel-button]:appearance-none",
          className,
        )}
        {...props}
      />

      {onScanClick && (
        <button
          type="button"
          onClick={onScanClick}
          // The visual label is an icon, so the accessible name must be explicit.
          aria-label="Scan or enter a barcode"
          className="grid size-8 shrink-0 place-items-center rounded-md text-ink-500 hover:bg-canvas hover:text-ink-900"
        >
          <ScanLine className="size-5" aria-hidden />
        </button>
      )}
    </div>
  );
}
